// ===================
// Collaboration Status
// ===================

export type CollaborationStatusKey =
  | 'not_started'
  | 'in_progress'
  | 'draft_complete'
  | 'in_review'
  | 'changes_requested'
  | 'approved'
  | 'complete'

export interface CollaborationStatusConfig {
  label: string
  dotColor: string
  variant: 'secondary' | 'warning' | 'default' | 'success' | 'destructive' | 'info' | 'review'
}

export const collaborationStatusConfig: Record<CollaborationStatusKey, CollaborationStatusConfig> = {
  not_started: { label: 'Not started', dotColor: 'bg-muted-foreground/20', variant: 'secondary' },
  in_progress: { label: 'In progress', dotColor: 'bg-[hsl(var(--status-warning))]', variant: 'warning' },
  draft_complete: { label: 'Draft complete', dotColor: 'bg-[hsl(var(--status-info))]', variant: 'info' },
  in_review: { label: 'In review', dotColor: 'bg-[hsl(var(--status-review))]', variant: 'review' },
  changes_requested: { label: 'Changes requested', dotColor: 'bg-destructive', variant: 'destructive' },
  approved: { label: 'Approved', dotColor: 'bg-[hsl(var(--status-success))]', variant: 'success' },
  complete: { label: 'Complete', dotColor: 'bg-[hsl(var(--status-success))]', variant: 'success' },
}

/**
 * The sections table's status pill while an AI suggestion waits on a section
 * (or a single document), shown in place of its status. Display only: the
 * section's own status is not changed.
 */
export const PENDING_SUGGESTION_STATUS = { label: 'Waiting on your review', variant: 'review' } as const

/**
 * The sections table's word count: the saved draft's, when it has words;
 * otherwise a waiting suggestion's, when the server sends one; otherwise the
 * saved figure as it is (null shows "—").
 */
export function tableWordCount(
  savedWordCount: number | null | undefined,
  pendingSuggestion?: { wordCount?: number | undefined } | null
): number | null {
  if (typeof savedWordCount === 'number' && savedWordCount > 0) return savedWordCount
  return pendingSuggestion?.wordCount ?? savedWordCount ?? null
}

export const COLLABORATION_STATUSES: Array<{ key: CollaborationStatusKey } & CollaborationStatusConfig> = (
  Object.entries(collaborationStatusConfig) as [CollaborationStatusKey, CollaborationStatusConfig][]
).map(([key, config]) => ({ key, ...config }))

// ===================
// Progress Buckets
// ===================

/**
 * The three buckets the response-document and questionnaire rollups count in.
 *
 * `toWrite` is what drives the "% drafted" figure, so the test is "does this
 * section still need writing?" — which is why `changes_requested` sits there
 * rather than with the drafted work. A reviewer sending a section back means
 * there is writing to do again, and the percentage should drop to say so.
 */
export type ProgressBucket = 'approved' | 'awaitingApproval' | 'toWrite'

const PROGRESS_BUCKET_BY_STATUS: Record<CollaborationStatusKey, ProgressBucket> = {
  not_started: 'toWrite',
  in_progress: 'toWrite',
  changes_requested: 'toWrite',
  draft_complete: 'awaitingApproval',
  in_review: 'awaitingApproval',
  approved: 'approved',
  complete: 'approved',
}

/**
 * Bucket a collaboration status. Unknown values (a status added server-side
 * before the frontend knows about it) count as still to write — the safe
 * direction, since it never overstates how much is finished.
 */
export function bucketForStatus(status: string): ProgressBucket {
  return PROGRESS_BUCKET_BY_STATUS[status as CollaborationStatusKey] ?? 'toWrite'
}

// ===================
// Section Progress (content-aware)
// ===================

/**
 * The buckets the response-document rollup and the My Work card count in.
 *
 * Unlike `bucketForStatus`, this looks at drafted content as well as status,
 * because AI drafting only ever moves a section to `in_progress`. A section
 * the AI has fully written therefore sits at `in_progress` with hundreds of
 * words — status alone would call it "still to write".
 *
 * `approved` and `complete` are kept apart so the rollup can name each
 * honestly: `complete` means someone marked it done, not that it was reviewed.
 */
export type SectionProgressBucket = 'approved' | 'complete' | 'awaitingApproval' | 'inProgress' | 'toWrite'

export interface SectionProgressInput {
  status: string
  /**
   * Drafted words. `undefined`/`null` means the call site doesn't know, and the
   * bucket falls back to status alone — which can only understate progress.
   */
  wordCount?: number | null | undefined
}

function hasDraftedWords(wordCount: number | null | undefined): boolean {
  return typeof wordCount === 'number' && Number.isFinite(wordCount) && wordCount > 0
}

/**
 * Bucket one section. Status past `in_progress` decides on its own; before
 * that, drafted words mean the section is written (in progress) rather than
 * still to write. `changes_requested` stays "to write" whatever its word count:
 * a reviewer sending it back means there is writing to do again.
 */
export function sectionProgressBucket({ status, wordCount }: SectionProgressInput): SectionProgressBucket {
  switch (status) {
    case 'approved':
      return 'approved'
    case 'complete':
      return 'complete'
    case 'draft_complete':
    case 'in_review':
      return 'awaitingApproval'
    case 'changes_requested':
      return 'toWrite'
    default:
      // not_started, in_progress, or a status the frontend doesn't know yet.
      return hasDraftedWords(wordCount) ? 'inProgress' : 'toWrite'
  }
}

/** Whether a section counts as drafted — anything not still to write. */
export function isSectionDrafted(input: SectionProgressInput): boolean {
  return sectionProgressBucket(input) !== 'toWrite'
}

export interface SectionProgressSummary {
  total: number
  counts: Record<SectionProgressBucket, number>
  drafted: number
  /** Whole-number percentage of sections drafted (0 when there are none). */
  draftedPercent: number
}

/** Roll a list of sections up into bucket counts and a drafted percentage. */
export function summariseSectionProgress(sections: SectionProgressInput[]): SectionProgressSummary {
  const counts: Record<SectionProgressBucket, number> = {
    approved: 0,
    complete: 0,
    awaitingApproval: 0,
    inProgress: 0,
    toWrite: 0,
  }
  for (const section of sections) {
    counts[sectionProgressBucket(section)] += 1
  }
  const total = sections.length
  const drafted = total - counts.toWrite
  const draftedPercent = total > 0 ? Math.round((drafted / total) * 100) : 0
  return { total, counts, drafted, draftedPercent }
}
